<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('marketCustomers.marketCustomersLabel') }}</span>
        <h3 class="page-title">{{ $t('marketCustomers.marketCustomersList') }}</h3>
      </d-col>

      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="showImportPopup = true">
          <i class="material-icons">cloud_upload</i>
          {{ $t('marketCustomers.importMarketCustomers') }}
        </d-button>
        <d-button
          theme="primary"
          @click.prevent="showImportHistoryPopup = true"
          class="ml-2">
          <i class="material-icons">history</i>
          {{ $t('marketCustomers.importHistory') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="$router.push('/klienci-z-targow/nowy')"
          class="ml-2">
          <i class="material-icons">add</i>
          {{ $t('marketCustomers.addNewMarketCustomer') }}
        </d-button>
      </d-col>
    </d-row>

    <validation-observer
      ref="market-customers-filters-form"
      tag="form"
      class="w-100">
      <d-row
        v-if="loaded"
        class="d-flex flex-nowrap ml-0 mb-3 page-filters w-100">
        <d-input
          :placeholder="$t('marketCustomers.marketCustomersSearchPlaceholder')"
          v-model="filters.search" />

        <d-input
          :placeholder="$t('customer.customerCountryQuestionMark')"
          v-model="filters.country"
          class="ml-2 w-25" />

        <date-range-picker
          :locale-data="{
            'firstDay': 1,
            'format': 'DD-MM-YYYY',
            'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
            'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
          }"
          class="ml-2 w-50"
          style="min-width: 300px;"
          :singleDatePicker="false"
          :timePicker="false"
          :showWeekNumbers="false"
          :showDropdowns="false"
          :autoApply="true"
          v-model="period"
          :ranges="false"
          :linkedCalendars="true">
          <template v-slot:input="picker" style="min-width: 300px;">
            {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
          </template>
        </date-range-picker>
      </d-row>

      <d-row
        v-if="loaded"
        class="d-flex flex-nowrap ml-0 mb-3 page-filters w-100">
        <d-input
          :placeholder="$t('marketCustomers.marketCustomersTagPlaceholder')"
          v-model="filters.tag" />

        <v-select
          :options="workers"
          v-model="filters.account_manager_id"
          :reduce="worker => worker.id"
          :filterBy="filterByForWorkers"
          :placeholder="$t('users.selectWorker')"
          label="name"
          class="w-50 ml-2">
          <template slot="no-options">
            {{ $t('users.noUsersMatchingPhrase') }}&hellip;
          </template>
          <template slot="option" slot-scope="option">
            <div class="d-center">
              {{ option.name }}
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">
              {{ option.name }}
            </div>
          </template>
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
      </d-row>
    </validation-observer>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('ui.name') }}</th>
            <th scope="col" class="border-0" style="width: 300px;">
              {{ $t('ui.emailAddress') }}<br>
              {{ $t('customer.telephone') }}
            </th>
            <th scope="col" class="border-0">{{ $t('marketCustomers.tag') }}</th>
            <th scope="col" class="border-0" style="width: 300px;">{{ $t('marketCustomers.otherData') }}</th>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('ui.lastContactAt') }}</th>
            <th scope="col" class="border-0" style="width: 240px;">{{ $t('marketCustomers.accountManager') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded && !loadError">
          <tr
            v-for="(customer, index) of customers"
            :key="'customer-' + index">
            <td class="align-middle">
              {{ customer.name }}
            </td>
            <td class="align-middle">
              <template v-if="customer.email">
                {{ customer.email }}<br>
              </template>
              {{ customer.phone }}
            </td>
            <td class="align-middle">
              {{ customer.tag }}
            </td>
            <td class="align-middle">
              <small>
                <template v-if="customer.contact_name || customer.contact_surname">
                  {{ customer.contact_name }} {{ customer.contact_surname }}<br>
                </template>

                <template v-if="customer.invoice_postcode || customer.invoice_city || customer.invoice_street || customer.invoice_building || customer.invoice_locale || customer.invoice_country_prefix || customer.invoice_tax_number || customer.country">
                  <template v-if="customer.invoice_street || customer.invoice_building || customer.invoice_locale">
                    {{ customer.invoice_street }} {{ customer.invoice_building }} <template v-if="customer.invoice_locale">/ {{ customer.invoice_locale }}</template><br>
                  </template>
                  <template v-if="customer.invoice_postcode || customer.invoice_city">{{ customer.invoice_postcode }} {{ customer.invoice_city }}<br></template>
                  <template v-if="customer.country">{{ customer.country }}<br></template>
                  {{ customer.invoice_country_prefix }} {{ customer.invoice_tax_number }}
                </template>
              </small>
            </td>
            <td class="align-middle">
              {{ customer.last_contact_at }}
            </td>
            <td class="align-middle">
              <template v-if="customer.account_manager">
              {{ customer.account_manager.name }}
              </template>
              <template v-else>
                &mdash;
              </template>
            </td>

            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/klienci-z-targow/edycja/' + customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="userIsAdmin"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeMarketCustomer(customer.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="customers.length === 0">
            <td
              colspan="7"
              class="text-center">
              {{ $t('marketCustomers.noMarketCustomersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded && !loadError" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning"
        class="text-center">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <import-popup
      v-if="showImportPopup" />

    <import-history-popup
      v-if="showImportHistoryPopup" />

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ImportHistoryPopup from './../components/market-customers/ImportHistoryPopup.vue';
import ImportPopup from './../components/market-customers/ImportPopup.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'users',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'import-history-popup': ImportHistoryPopup,
    'import-popup': ImportPopup,
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers;
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    }
  },
  data() {
    return {
      customers: [],
      loaded: false,
      loadError: false,
      loadedWorkers: false,
      showImportHistoryPopup: false,
      showImportPopup: false,
      removeInProgress: false,
      workers: [],
      filters: {
        search: '',
        country: '',
        account_manager_id: null,
        tag: ''
      },
      period: {
        startDate: null,
        endDate: null
      }
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.loadFilteredData();
    this.$bus.$on('market-customers-reload-needed', this.resetPagination);
    this.$bus.$on('market-customers-import-popup-close', this.closeImportPopup);
    this.$bus.$on('market-customers-import-history-popup-close', this.closeImportHistoryPopup);
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'workers',
        loadedKey: 'loadedWorkers',
        errorKey: 'loadError',
        params: {
          return: 'get',
          where: {
            type: ['admin', 'worker']
          }
        },
        successAction: () => {
          this.workers = [{
            id: 0,
            name: this.$t('ui.lack')
          }].concat(this.workers);
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search,
        tag: this.filters.tag
      };

      if (this.filters.account_manager_id || this.filters.account_manager_id === 0) {
        where.account_manager_id = this.filters.account_manager_id;
      }

      if (this.filters.country) {
        where.country = this.filters.country;
      }

      if (this.period.startDate && this.period.endDate) {
        where.last_contact_from = [
          this.period.startDate.getFullYear(),
          this.period.startDate.getMonth() + 1,
          this.period.startDate.getDate()
        ].map(n => n < 10 ? '0' + n : n).join('-');
        where.last_contact_to = [
          this.period.endDate.getFullYear(),
          this.period.endDate.getMonth() + 1,
          this.period.endDate.getDate()
        ].map(n => n < 10 ? '0' + n : n).join('-');
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/market-customers/items?page=' + this.currentPage,
        listField: 'customers',
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        country: '',
        tag: '',
        account_manager_id: null
      };

      this.period.startDate = null;
      this.period.endDate = null;
      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['market-customers-filters-form'], this.debouncedLoadFilteredData);
    },
    removeMarketCustomer (customerID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/market-customers/delete/',
        id: customerID,
        successTitle: this.$t('marketCustomers.deleteSuccessTitle'),
        successText: this.$t('marketCustomers.deleteSuccessText'),
        confirmTitle: this.$t('marketCustomers.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('marketCustomers.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    filterByForWorkers (option, label, search) {
      return (option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    closeImportHistoryPopup () {
      this.showImportHistoryPopup = false;
    },
    closeImportPopup () {
      this.showImportPopup = false;
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  },
  beforeDestroy () {
    this.$bus.$off('market-customers-reload-needed', this.resetPagination);
    this.$bus.$off('market-customers-import-popup-close', this.closeImportPopup);
    this.$bus.$off('market-customers-import-history-popup-close', this.closeImportHistoryPopup);
  }
};
</script>
